import React, { useState, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Sidebar from "./components/Sidebar";
import LegalGate from "./components/LegalGate";
import MaintenanceBanner from "./components/MaintenanceBanner";
import NavBar from "./components/NavBar";
import { ProjectProvider } from "./context/ProjectContext";
import LoginPage from "./pages/Login/LoginPage";
import PersonaHandoff from "./pages/Login/PersonaHandoff";
import { signupOptionsForApp } from "./constants/personaRoutes";
import { getAppId } from "./constants/appIdentity";
import RouteFallback from "./components/RouteFallback";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import DiscoveryPromptBanner from "./pages/InteriorDesigner/DiscoveryPromptBanner";
// Work marketplace — Architect (Party A) + Freelancer (Party B) + admin
import { UserProvider, useUser } from "./context/UserContext";
import PrivateRoute from "./utils/PrivateRoute";
import Unauthorized from "./components/Unauthorized";
import { SalesProvider } from "./context/SalesContext";
import Footer from "./components/Footer";

// ── Route components, code-split ──────────────────────────────────────────────
// Every page below is loaded on demand. Previously all ~110 were static imports,
// so one 6.2 MB chunk was downloaded by every persona before anything rendered —
// an architect paid for ag-grid, tiptap, quill and chart.js to see nine screens.
// CRA's default splitChunks pulls each page and its exclusive dependencies into
// their own chunk once the import is dynamic.
//
// NavBar, Sidebar, Footer, LoginPage, Unauthorized and PrivateRoute stay static:
// they render on essentially every route, so deferring them buys nothing and adds
// a flash of fallback. PrivateRoute especially — lazy-loading a route guard means
// the guard itself arrives after the decision to render is already being made.
const LegalPage = lazy(() => import("./pages/Legal/LegalPage"));
// Play requires a publicly reachable account-deletion URL, so this is registered in the
// unauthenticated route block as well as the authenticated one.
const AccountDeletionPage = lazy(() => import("./pages/Legal/AccountDeletionPage"));
const ProjectList = lazy(() => import("./pages/Projects/ProjectList"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const Configs = lazy(() => import("./pages/Projects/Configurations/Configs"));
const Properties = lazy(() => import("./pages/Properties/Properties"));
const AgentList = lazy(() => import("./pages/Agents/AgentList"));
const RolesDashboard = lazy(() => import("./pages/RoleManagement/RolesDashboard"));
const CustomerDashboard = lazy(() => import("./pages/Customers/CustomerDashboard"));
const SalesDashboard = lazy(() => import("./pages/Sales/SalesDashboard"));
const Settings = lazy(() => import("./pages/Settings/Settings"));
const ProjectDetails = lazy(() => import("./pages/Projects/ProjectDetails"));
const UserPage = lazy(() => import("./pages/Customers/UserPage"));
const ConfigDetails = lazy(() => import("./components/ProjectScreens/ConfigDetails"));
const InteriorDesignerSignup = lazy(() => import("./pages/Login/InteriorDesignerSignup"));
const ArchitectSignup = lazy(() => import("./pages/Login/ArchitectSignup"));
const FreelancerSignup = lazy(() => import("./pages/Login/FreelancerSignup"));
const ArchitectDashboard = lazy(() => import("./pages/Architect/ArchitectDashboard"));
const ArchitectNewProject = lazy(() => import("./pages/Architect/NewProject"));
const ArchitectProjectDetail = lazy(() => import("./pages/Architect/ArchitectProjectDetail"));
const ArchitectWallet = lazy(() => import("./pages/Architect/ArchitectWallet"));
const ArchitectProfile = lazy(() => import("./pages/Architect/ArchitectProfile"));
const FreelancerDashboard = lazy(() => import("./pages/Freelancer/FreelancerDashboard"));
const FreelancerSkills = lazy(() => import("./pages/Freelancer/FreelancerSkills"));
const FreelancerAssignedProject = lazy(() => import("./pages/Freelancer/FreelancerAssignedProject"));
const FreelancerProfile = lazy(() => import("./pages/Freelancer/FreelancerProfile"));
const MarketplaceAdmin = lazy(() => import("./pages/ZumaleAdmin/MarketplaceAdmin"));
const SubscriptionsInfoPage = lazy(() => import("./pages/Subscriptions/SubscriptionsInfoPage"));
const AgentDashboard = lazy(() => import("./pages/AgentView/AgentDashboard"));
const UserDashboard = lazy(() => import("./pages/UserView/UserDashboard"));
const CreateProject = lazy(() => import("./pages/Projects/CreateProject"));
const PropertyUserView = lazy(() => import("./pages/UserView/PropertyUserView"));
const Profile = lazy(() => import("./pages/Profile/Profile"));
const SoldPropertyDetails = lazy(() => import("./pages/Properties/SoldPropertyDetails"));
// Consolidated Zumale-admin sections. Each hosts the pages it replaced as tabs; the old
// routes below redirect into the matching tab so existing links keep working.
const AdminCustomers = lazy(() => import("./pages/ZumaleAdmin/AdminCustomers"));
const AdminReferrals = lazy(() => import("./pages/ZumaleAdmin/AdminReferrals"));
const AdminInteriorDesign = lazy(() => import("./pages/ZumaleAdmin/AdminInteriorDesign"));
const ZumaleAdminDashboard = lazy(() => import("./pages/ZumaleAdmin/ZumaleAdminDashboard"));
const ZumaleAdminReports = lazy(() => import("./pages/ZumaleAdmin/ZumaleAdminReports"));
const InteriorExecutionDetailAdmin = lazy(() => import("./pages/ZumaleAdmin/InteriorExecutionDetailAdmin"));
const InteractionDetailReadOnly = lazy(() => import("./pages/ZumaleAdmin/InteractionDetailReadOnly"));
const ZumaleSettings = lazy(() => import("./pages/ZumaleAdmin/ZumaleSettings"));
const MaterialVendors = lazy(() => import("./pages/ZumaleAdmin/MaterialVendors"));
const ResaleUnits = lazy(() => import("./pages/ZumaleAdmin/ResaleUnits"));
const SellerLeads = lazy(() => import("./pages/ZumaleAdmin/SellerLeads"));
const TenderQuotePage = lazy(() => import("./pages/Public/TenderQuotePage"));
const AgreementTemplate = lazy(() => import("./pages/ZumaleAdmin/AgreementTemplate"));
const Requests = lazy(() => import("./pages/ZumaleAdmin/Requests"));
const AdminProjects = lazy(() => import("./pages/ZumaleAdmin/AdminProjects"));
const InternalOwners = lazy(() => import("./pages/ZumaleAdmin/InternalOwners"));
const ReferralCommissions = lazy(() => import("./pages/AgentView/ReferralCommissions"));
const AdminProjectDetail = lazy(() => import("./pages/ZumaleAdmin/AdminProjectDetail"));
const Reports = lazy(() => import("./pages/Reports/Reports"));
const PerformanceDashBoard = lazy(() => import("./pages/Reports/Performance/PerformanceDashboard"));
const SalesReport = lazy(() => import("./pages/Reports/SalesReport"));
const CompletionReport = lazy(() => import("./pages/Reports/CompletionReport"));
const ViewCustomer = lazy(() => import("./pages/ZumaleUser/ViewCustomer"));
const Communications = lazy(() => import("./pages/Communications/Communications"));
const Advertisements = lazy(() => import("./pages/Advertisements/Advertisements"));
const Template = lazy(() => import("./pages/Communications/Whatsapp/Template"));
const WhatsAppSetup = lazy(() => import("./pages/WhatsAppSetup/WhatsAppSetup"));
const Approval = lazy(() => import("./pages/Approval/Approval"));
const Workflow = lazy(() => import("./pages/Communications/Whatsapp/Workflow"));
const CreateWorkflow = lazy(() => import("./pages/Communications/Whatsapp/CreateWorkflow"));
const SiteVisit = lazy(() => import("./pages/SiteVisit"));
const PhaseDetails = lazy(() => import("./pages/Projects/PhaseDetails"));
const InteriorDesignerDashboard = lazy(() => import("./pages/InteriorDesigner/InteriorDesignerDashboard"));
const Phases = lazy(() => import("./pages/InteriorDesigner/Phases"));
const InteriorDesignerPhaseDetails = lazy(() => import("./pages/InteriorDesigner/PhaseDetails"));
const InteriorDesignerConfigDetails = lazy(() => import("./pages/InteriorDesigner/ConfigDetails"));
const Leads = lazy(() => import("./pages/InteriorDesigner/Leads"));
const LeadRequestDetails = lazy(() => import("./pages/InteriorDesigner/LeadRequestDetails"));
const Designs = lazy(() => import("./pages/InteriorDesigner/Designs"));
const InteriorDesignerSettings = lazy(() => import("./pages/InteriorDesigner/InteriorDesignerSettings"));
const AddPortfolio = lazy(() => import("./pages/InteriorDesigner/AddPortfolio"));
const AddPortfolioBoq = lazy(() => import("./pages/InteriorDesigner/AddPortfolioBoq"));
const BVPhaseList = lazy(() => import("./pages/BV_user/BV_PhaseList"));
const BV_PhaseDetails = lazy(() => import("./pages/BV_user/BV_PhaseDetails"));
const BV_ConfigDetails = lazy(() => import("./pages/BV_user/BV_ConfigDetails"));
const BuyerDetails = lazy(() => import("./pages/BV_user/BuyerDetails"));
const Buyers = lazy(() => import("./pages/BV_user/Buyers"));
const RequestDetails = lazy(() => import("./pages/BV_user/RequestDetails"));
const ExecutionUnits = lazy(() => import("./pages/BV_user/ExecutionUnits"));
const ExecutionUnitDetail = lazy(() => import("./pages/BV_user/ExecutionUnitDetail"));
// v4_6 execution-vendor layer — BV supervision surfaces.
const ExecutionVendorBoard = lazy(() => import("./pages/BV_user/ExecutionVendorBoard"));
const ProgressApprovals = lazy(() => import("./pages/BV_user/ProgressApprovals"));
const VendorPayouts = lazy(() => import("./pages/BV_user/VendorPayouts"));
// v4_6 — the execution vendor's own portal.
const VendorLayout = lazy(() => import("./pages/Vendor/VendorLayout"));
const VendorSites = lazy(() => import("./pages/Vendor/VendorSites"));
const VendorAwardDetail = lazy(() => import("./pages/Vendor/VendorAwardDetail"));
const VendorWorkItems = lazy(() => import("./pages/Vendor/VendorWorkItems"));
const VendorTeam = lazy(() => import("./pages/Vendor/VendorTeam"));
const VendorPaymentsView = lazy(() => import("./pages/Vendor/VendorPaymentsView"));
const UnitExecutionDashboard = lazy(() => import("./pages/Projects/UnitExecutionDashboard"));
const ExecutionPlannerScreen = lazy(() => import("./pages/Projects/ExecutionPlannerScreen"));


// F18 (v2): Interior execution routes

function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleSidebar = () => {
    setIsSidebarOpen((prevState) => !prevState);
  };

  return (
    <UserProvider>
      <SalesProvider>
        <Router>
          <div className="min-h-screen bg-gray-100 flex flex-col">
            <AppRoutes
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={toggleSidebar}
            />
          </div>
        </Router>
      </SalesProvider>
    </UserProvider>
  );
}

// Slug -> screen. Kept beside SIGNUP_OPTIONS in personaRoutes.js: that file decides
// WHICH signups a surface offers, this decides what each one renders.
const SIGNUP_SCREENS = {
  "interior-designer": <InteriorDesignerSignup />,
  architect: <ArchitectSignup />,
  freelancer: <FreelancerSignup />,
};

function AppRoutes({ isSidebarOpen, toggleSidebar }) {
  const location = useLocation();
  const { userInfo } = useUser();

  // Signup routes for THIS surface only, shared by both route blocks below.
  // Declared before the early returns: the unauthenticated block below renders it,
  // and a const referenced above its declaration is a temporal-dead-zone crash,
  // not a hoisted undefined.
  const signupRoutes = signupOptionsForApp(getAppId()).map((option) => (
    <Route
      key={option.slug}
      path={`/signup/${option.slug}`}
      element={SIGNUP_SCREENS[option.slug]}
    />
  ));

  // Standalone, token-gated vendor tender-quote page — renders with no app chrome and
  // regardless of auth (vendors open it from an emailed magic link without logging in).
  if (location.pathname.startsWith("/tender/")) {
    return (
      <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/tender/:token" element={<TenderQuotePage />} />
      </Routes>
      </Suspense>
      </ChunkErrorBoundary>
    );
  }

  // Gate the public/unauthenticated route set on whether we have an active persona.
  const persona = userInfo?.persona;

  if (!persona) {
    return (
      <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
      <>
      {/* Also shown signed-out: somebody who cannot get in during a window should be told why,
          rather than assuming their password is wrong. */}
      <MaintenanceBanner />
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/switch" element={<PersonaHandoff />} />
        {/* Public. Someone must be able to read the terms before accepting them,
            and a store reviewer has no account at all. */}
        <Route path="/legal/privacy" element={<LegalPage documentKey="privacy" />} />
        <Route path="/legal/terms" element={<LegalPage documentKey="terms" />} />
        <Route path="/legal/terms/:annex" element={<LegalPage documentKey="terms" />} />
        {/* Play's required public deletion URL. Must work with no account — someone who
            has already uninstalled the app has no other way to ask. */}
        <Route path="/legal/delete-account" element={<AccountDeletionPage />} />
        {/* Only the signups this surface serves. Registering the others would let
            someone deep-link to a form the backend refuses on submit — a wasted
            form-fill rather than an honest "not here". */}
        {signupRoutes}
        <Route path="/subscriptions" element={<SubscriptionsInfoPage />} />
        <Route path="/site-visit" element={<SiteVisit />} />

      </Routes>
      {/* The authenticated layout renders the Footer, but this branch returns before reaching it —
          so every signed-out page had no legal links at all. That is backwards: sign-in and sign-up
          are exactly where someone needs to read the terms they are about to accept, and the store
          listings require the privacy policy and the deletion route to be reachable with no
          account. These pages are `min-h-screen`, so it sits below the fold, where a footer belongs. */}
      <Footer />
      </>
      </Suspense>
      </ChunkErrorBoundary>
    );
  }

  const isLoginOrRegisterPage =
    location.pathname === "/" ||
    location.pathname === "/subscriptions" ||
    location.pathname === "/site-visit" ||
    // Cross-domain persona handoff — a spinner that immediately redirects.
    // Rendering the full chrome around it makes the app flash a shell for the
    // OLD persona before switching to the new one.
    location.pathname === "/switch" ||
    // Legal documents are public and read without an account — rendering the
    // authenticated chrome around them would be wrong for a signed-out reader
    // and for a store reviewer.
    location.pathname.startsWith("/legal");

  return (
    // <ProjectProvider>
    <>
      {/* Terms / Privacy acceptance (v3_97). Mounted here — after the unauthenticated early return,
          so `persona` is resolved — because it is the one place that covers EVERY authenticated
          route, including the three that have no route guard of their own. It renders nothing
          unless the server says something is outstanding, and the server-side gate in verify_access
          is the real enforcement; this is only its surface. */}
      {!isLoginOrRegisterPage && <LegalGate />}
      {/* Planned-maintenance notice (v4_3). Above the chrome so it is the first thing read, and
          outside the legal gate so a blocked user still learns the platform is going down. */}
      <MaintenanceBanner />
      <div className="flex flex-col min-h-screen bg-gray-100 page-container">
        {!isLoginOrRegisterPage && <NavBar toggleSidebar={toggleSidebar} />}

        {/* Sidebar */}
        {!isLoginOrRegisterPage && (
          <Sidebar
            isOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
            className={`fixed top-0 left-0 z-50 h-full bg-white transition-transform duration-300 ${isSidebarOpen ? "translate-x-0 w-64" : "-translate-x-64 w-0"
              } sm:translate-x-0 sm:w-64`}
          />
        )}

        {/* Dark Overlay (only over content area, excluding sidebar) */}
        {isSidebarOpen && !isLoginOrRegisterPage && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50 sm:ml-64"
            onClick={toggleSidebar} // Close the sidebar when clicking on the overlay
          />
        )}

        {/* Content
            `flex-grow` fills the vertical space between NavBar and Footer so
            the Footer sits flush at the bottom of the viewport (no manual
            spacer needed). The inner wrapper caps the page width at 1536px
            and centres it on wide monitors — prevents the edge-to-edge
            sprawl that happens on 24"+ screens. */}
        {/* Horizontal padding is the whole ballgame on a phone, so it is spelled out rather
            than layered.
            Before, this element carried `p-8 pl-8 sm:pl-16` AND a conditional `pl-64`/`pl-0` in
            the same class string. Four left-padding utilities at once: which one won depended on
            their order in the generated stylesheet, not on the order written here. Worse, `pl-64`
            applied at EVERY width — so opening the drawer on a 360px phone, where it is an
            overlay and reserves no space, squeezed the content into about 100px.
            Now: padding scales with the viewport, and the sidebar offset is applied only from
            `sm` up, where the rail actually occupies layout space. */}
        <div
          className={`flex-grow px-4 pb-6 pt-20 sm:px-6 sm:pb-8 lg:px-8 transition-all duration-300 content-area ${
            !isLoginOrRegisterPage ? (isSidebarOpen ? "sm:pl-64" : "sm:pl-16") : ""
          }`}
        >
         <div className="w-full max-w-screen-2xl mx-auto">
          {/* Interior designers who aren't discoverable yet see this above every page — the
              subscription that gets them in front of BuildVizer's own buyers was previously
              only mentioned part-way down the Phases tab. Renders null for everyone else. */}
          {!isLoginOrRegisterPage && <DiscoveryPromptBanner />}
          <ChunkErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />
        <Route path="/switch" element={<PersonaHandoff />} />
        {/* Public. Someone must be able to read the terms before accepting them,
            and a store reviewer has no account at all. */}
        <Route path="/legal/privacy" element={<LegalPage documentKey="privacy" />} />
        <Route path="/legal/terms" element={<LegalPage documentKey="terms" />} />
        <Route path="/legal/terms/:annex" element={<LegalPage documentKey="terms" />} />
        {/* Play's required public deletion URL. Must work with no account — someone who
            has already uninstalled the app has no other way to ask. */}
        <Route path="/legal/delete-account" element={<AccountDeletionPage />} />
            {signupRoutes}
            <Route path="/subscriptions" element={<SubscriptionsInfoPage />} />
            <Route path="/site-visit" element={<SiteVisit />} />

            {/* Work marketplace — Architect (Party A) */}
            <Route
              path="/architect/dashboard"
              element={<PrivateRoute allowedRoles={["ARCHITECT"]}><ArchitectDashboard /></PrivateRoute>}
            />
            <Route
              path="/architect/projects/new"
              element={<PrivateRoute allowedRoles={["ARCHITECT"]}><ArchitectNewProject /></PrivateRoute>}
            />
            <Route
              path="/architect/projects/:projectId"
              element={<PrivateRoute allowedRoles={["ARCHITECT"]}><ArchitectProjectDetail /></PrivateRoute>}
            />
            <Route
              path="/architect/wallet"
              element={<PrivateRoute allowedRoles={["ARCHITECT"]}><ArchitectWallet /></PrivateRoute>}
            />
            <Route
              path="/architect/profile"
              element={<PrivateRoute allowedRoles={["ARCHITECT"]}><ArchitectProfile /></PrivateRoute>}
            />

            {/* Work marketplace — Freelancer (Party B) */}
            <Route
              path="/freelancer/dashboard"
              element={<PrivateRoute allowedRoles={["FREELANCER"]}><FreelancerDashboard /></PrivateRoute>}
            />
            <Route
              path="/freelancer/skills"
              element={<PrivateRoute allowedRoles={["FREELANCER"]}><FreelancerSkills /></PrivateRoute>}
            />
            <Route
              path="/freelancer/projects/:projectId"
              element={<PrivateRoute allowedRoles={["FREELANCER"]}><FreelancerAssignedProject /></PrivateRoute>}
            />
            <Route
              path="/freelancer/profile"
              element={<PrivateRoute allowedRoles={["FREELANCER"]}><FreelancerProfile /></PrivateRoute>}
            />

            {/* Work marketplace — Zumale admin */}
            <Route
              path="/zumale-admin/marketplace"
              element={<PrivateRoute allowedRoles={["ZUMALE_USER"]}><MarketplaceAdmin /></PrivateRoute>}
            />

            <Route
              path="/profile"
              element={
                <PrivateRoute
                  allowedRoles={[
                    "ADMIN",
                    "ZUMALE_USER",
                    "USER",
                    "AGENT",
                    "SALES",
                    "DEVELOPER",
                    "INTERIOR_DESIGNER",
                    "BV_USER",
                  ]}
                >
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/properties/sold/:customerSaleId"
              element={
                <PrivateRoute
                  allowedRoles={["ADMIN", "SALES", "AGENT", "DEVELOPER"]}
                >
                  <SoldPropertyDetails />
                </PrivateRoute>
              }
            />

            {/* <Route path="/developer-dashboard" element={<PrivateRoute allowedRoles={["ADMIN", "DEVELOPER"]}><Dashboard /></PrivateRoute>} /> */}
            <Route
              path="/admin-dashboard"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER"]}>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <PrivateRoute
                  allowedRoles={["ADMIN", "SALES", "AGENT", "DEVELOPER"]}
                >
                  <ProjectList />
                </PrivateRoute>
              }
            />
            <Route
              path="/create-project"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER"]}>
                  <CreateProject />
                </PrivateRoute>
              }
            />
            <Route
              path="/project-details/:projectId"
              element={
                <ProjectProvider>
                  <PrivateRoute
                    allowedRoles={[
                      "USER",
                      "ADMIN",
                      "AGENT",
                      "SALES",
                      "DEVELOPER",
                      "PRESALES_SALES",
                    ]}
                  >
                    <ProjectDetails />
                  </PrivateRoute>
                </ProjectProvider>
              }
            />
            <Route
              path="/agents"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "SALES", "DEVELOPER"]}>
                  <AgentList />
                </PrivateRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER"]}>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/configurations"
              element={
                <PrivateRoute
                  allowedRoles={["ADMIN", "SALES", "AGENT", "DEVELOPER", "PRESALES_SALES"]}
                >
                  <Configs />
                </PrivateRoute>
              }
            />
            <Route
              path="/properties"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "SALES", "DEVELOPER", "PRESALES", "PRESALES_SALES"]}>
                  <Properties />
                </PrivateRoute>
              }
            />

            {/* <Route path="/projects/notifications" element={<PrivateRoute allowedRoles={["SALES", "ADMIN", "DEVELOPER"]}><Notifications /></PrivateRoute>} /> */}
            <Route path="/communications" element={<PrivateRoute allowedRoles={["SALES", "ADMIN", "DEVELOPER", "PRESALES", "PRESALES_SALES"]}><Communications /></PrivateRoute>} />
            <Route path="/advertisements" element={<PrivateRoute allowedRoles={["SALES", "ADMIN", "DEVELOPER"]}><Advertisements /></PrivateRoute>} />
            <Route path="/leads" element={<Workflow />} />
            <Route path="/template/:templateType" element={<Template />} />
            <Route path="/workflow/:leadType" element={<CreateWorkflow />} />
            <Route
              path="/projects/:projectId/configs/:configId"
              element={
                <PrivateRoute
                  allowedRoles={["SALES", "ADMIN", "AGENT", "DEVELOPER"]}
                >
                  <ConfigDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/role-management"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER"]}>
                  <RolesDashboard />
                </PrivateRoute>
              }
            />
            {/* /manage-roles moved into Settings → Role Based Access (see Profile.jsx).
                Redirect any old links into the Settings tab. */}
            <Route path="/manage-roles" element={<Navigate to="/profile#rbac" replace />} />
            <Route
              path="/customers"
              element={
                <PrivateRoute
                  allowedRoles={["ADMIN", "SALES", "AGENT", "DEVELOPER", "PRESALES", "PRESALES_SALES"]}
                >
                  <CustomerDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/user-details/:id"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "SALES", "DEVELOPER"]}>
                  <UserPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "SALES", "DEVELOPER", "PRESALES_SALES"]}>
                  <SalesDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/agent-dashboard"
              element={
                <PrivateRoute allowedRoles={["AGENT"]}>
                  <AgentDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/referral-commissions"
              element={
                <PrivateRoute allowedRoles={["AGENT"]}>
                  <ReferralCommissions />
                </PrivateRoute>
              }
            />

            <Route
              path="/sales-dashboard"
              element={
                <PrivateRoute allowedRoles={["SALES"]}>
                  <SalesDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/presales-dashboard"
              element={
                <PrivateRoute allowedRoles={["PRESALES"]}>
                  <SalesDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/presales-sales-dashboard"
              element={
                <PrivateRoute allowedRoles={["PRESALES_SALES"]}>
                  <SalesDashboard />
                </PrivateRoute>
              }
            />


            <Route
              path="/user-dashboard"
              element={
                <PrivateRoute allowedRoles={["USER"]}>
                  <UserDashboard />
                </PrivateRoute>
              }
            />
            {/* <Route path="/user-agreements" element={<PrivateRoute allowedRoles={["USER"]}><AgreementApprov /></PrivateRoute>} /> */}
            <Route
              path="/user-property"
              element={
                <PrivateRoute allowedRoles={["USER"]}>
                  <PropertyUserView />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin-dashboard"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <ZumaleAdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/settings"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <ZumaleSettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/agreements"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <AgreementTemplate />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/requests"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <Requests />
                </PrivateRoute>
              }
            />
            <Route path="/zumale-admin/team" element={<Navigate to="/zumale-admin/settings#team" replace />} />
            <Route
              path="/zumale-admin/projects"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <AdminProjects />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/internal-owners"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <InternalOwners />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/projects/:projectId"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <AdminProjectDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/reports"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <ZumaleAdminReports />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/interior-design"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <AdminInteriorDesign />
                </PrivateRoute>
              }
            />
            <Route path="/zumale-admin/interior-executions" element={<Navigate to="/zumale-admin/interior-design#executions" replace />} />
            <Route
              path="/zumale-admin/interior-executions/interactions/:leadId"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <InteractionDetailReadOnly />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/interior-executions/:leadId"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <InteriorExecutionDetailAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/material-vendors"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <MaterialVendors />
                </PrivateRoute>
              }
            />
            <Route path="/zumale-admin/referral-vendors" element={<Navigate to="/zumale-admin/referrals#partners" replace />} />
            <Route path="/zumale-admin/cp-plus" element={<Navigate to="/zumale_user-dashboard#cp-plus" replace />} />
            <Route path="/zumale-admin/id-plus" element={<Navigate to="/zumale_user-dashboard#id-plus" replace />} />
            <Route
              path="/zumale-admin/resale-units"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <ResaleUnits />
                </PrivateRoute>
              }
            />
            <Route
              path="/zumale-admin/seller-leads"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <SellerLeads />
                </PrivateRoute>
              }
            />
            <Route path="/zumale-admin/referral-rewards" element={<Navigate to="/zumale-admin/referrals#rewards" replace />} />
            <Route path="/zumale-admin/referral-redemptions" element={<Navigate to="/zumale-admin/referrals#redemptions" replace />} />
            <Route
              path="/zumale-admin/referrals"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <AdminReferrals />
                </PrivateRoute>
              }
            />
            <Route path="/zumale-admin/interior-designers" element={<Navigate to="/zumale-admin/interior-design#designers" replace />} />
            <Route
              path="/zumale_user-dashboard"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <AdminCustomers />
                </PrivateRoute>
              }
            />
            {/* Reports — single sidebar entry; nested vertical nav lives inside
                the Reports layout. Sub-routes are children so URLs like
                /reports/sales still deep-link directly. */}
            <Route
              path="/reports"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "SALES", "PRESALES_SALES"]}>
                  <Reports />
                </PrivateRoute>
              }
            >
              <Route path="sales" element={<SalesReport />} />
              <Route path="performance" element={<PerformanceDashBoard />} />
              <Route path="completion" element={<CompletionReport />} />
            </Route>
            <Route
              path="/project-details/:projectId"
              element={
                <ProjectProvider>
                  <PrivateRoute
                    allowedRoles={[
                      "USER",
                      "ADMIN",
                      "AGENT",
                      "SALES",
                      "DEVELOPER",
                    ]}
                  >
                    <ProjectDetails />
                  </PrivateRoute>
                </ProjectProvider>
              }
            />
            <Route
              path="/project-details/:projectId/phase/:phaseId"
              element={
                <ProjectProvider>
                  <PrivateRoute
                    allowedRoles={[
                      "USER",
                      "ADMIN",
                      "AGENT",
                      "SALES",
                      "DEVELOPER",
                    ]}
                  >
                    <PhaseDetails />
                  </PrivateRoute>
                </ProjectProvider>
              }
            />
            {/* F18 (v2): Interior execution engine routes */}
            <Route
              path="/unit-execution/:propertyId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "SALES"]}>
                  <UnitExecutionDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/execution-planner/:phaseId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER"]}>
                  <ExecutionPlannerScreen />
                </PrivateRoute>
              }
            />

            <Route
              path="/customers/view/:customerId"
              element={
                <PrivateRoute allowedRoles={["ZUMALE_USER"]}>
                  <ViewCustomer />
                </PrivateRoute>
              }
            />
            <Route path="/approvals" element={
              <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "SALES"]}>
                <Approval />
              </PrivateRoute>
            }
            />
            <Route path="/whatsapp/setup" element={
              <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER"]}>
                <WhatsAppSetup />
              </PrivateRoute>
            }
            />
            <Route
              path="/interior-designer-dashboard"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <InteriorDesignerDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/phases"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <Phases />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/phases/:projectId/:phaseId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <InteriorDesignerPhaseDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/phases/:projectId/configs/:configId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <InteriorDesignerConfigDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/leads"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <Leads />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/leads/:leadId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <LeadRequestDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/designs"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <Designs />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/settings"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <InteriorDesignerSettings />
                </PrivateRoute>
              }
            />
            {/* Legacy /interior-designer/profile — redirect to /settings#profile
                so old deep links and bookmarks still land users on their account. */}
            <Route
              path="/interior-designer/profile"
              element={<Navigate to="/interior-designer/settings#profile" replace />}
            />
            <Route
              path="/interior-designer/add-portfolio"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <AddPortfolio />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/edit-portfolio/:portfolioId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <AddPortfolio />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-designer/add-portfolio/:portfolioId/boq"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "INTERIOR_DESIGNER"]}>
                  <AddPortfolioBoq />
                </PrivateRoute>
              }
            />
            {/* BV_USER routes - pointing to same components as interior-designer */}
            <Route
              path="/bv-user/phases"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "BV_USER"]}>
                  <BVPhaseList />
                </PrivateRoute>
              }
            />
            {/* configs before :phaseId so "configs" is not captured as a phase id */}
            <Route
              path="/bv-user/phases/:projectId/configs/:configId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "BV_USER"]}>
                  {/* eslint-disable-next-line react/jsx-pascal-case */}
                  <BV_ConfigDetails />
                </PrivateRoute>
              }
            />
            {/* Same /bv-user/phases prefix as the list — avoids /bv-user/phase/... 404 → Unauthorized */}
            <Route
              path="/bv-user/phases/:phaseId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "BV_USER"]}>
                  {/* eslint-disable-next-line react/jsx-pascal-case */}
                  <BV_PhaseDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/bv-user/buyers"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "BV_USER"]}>
                  <Buyers />
                </PrivateRoute>
              }
            />
            <Route
              path="/bv-user/buyer/:buyerId/details"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "BV_USER"]}>
                  <BuyerDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-execution"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "BV_USER", "ZUMALE_USER"]}>
                  <ExecutionUnits />
                </PrivateRoute>
              }
            />
            <Route
              path="/interior-execution/:leadId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "BV_USER", "ZUMALE_USER"]}>
                  <ExecutionUnitDetail />
                </PrivateRoute>
              }
            />

            {/* v4_6 — execution vendors, seen across every unit rather than one at a time. */}
            <Route
              path="/execution-vendors"
              element={
                <PrivateRoute allowedRoles={["BV_USER", "ZUMALE_USER"]}>
                  <ExecutionVendorBoard />
                </PrivateRoute>
              }
            />
            <Route
              path="/execution-vendors/approvals"
              element={
                <PrivateRoute allowedRoles={["BV_USER", "ZUMALE_USER"]}>
                  <ProgressApprovals />
                </PrivateRoute>
              }
            />
            <Route
              path="/execution-vendors/payouts"
              element={
                <PrivateRoute allowedRoles={["BV_USER", "ZUMALE_USER"]}>
                  <VendorPayouts />
                </PrivateRoute>
              }
            />

            {/* v4_6 — the execution vendor's own portal. Web-first: these vendors already meet
                BuildVizer through a phone browser on the magic-link quote page. */}
            <Route
              path="/vendor"
              element={
                <PrivateRoute allowedRoles={["VENDOR"]}>
                  <VendorLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="/vendor/sites" replace />} />
              <Route path="sites" element={<VendorSites />} />
              <Route path="sites/:awardId" element={<VendorAwardDetail />} />
              <Route path="work" element={<VendorWorkItems />} />
              <Route path="team" element={<VendorTeam />} />
              <Route path="payments" element={<VendorPaymentsView />} />
            </Route>
            <Route
              path="/bv-user/buyer/:buyerId/requests/:interiorDesignerId"
              element={
                <PrivateRoute allowedRoles={["ADMIN", "DEVELOPER", "BV_USER"]}>
                  <RequestDetails />
                </PrivateRoute>
              }
            />

            <Route path="*" element={<Unauthorized />} />
          </Routes>
          </Suspense>
          </ChunkErrorBoundary>
         </div>

        </div>

        {/* Footer lives INSIDE the flex column so flex-grow on the content
            area pushes it to the bottom of the viewport. The previous layout
            placed it in a sibling div with pt-80, which produced ~320px of
            dead space above the footer on tall screens. */}
        {/* Always rendered. The legal links live here and must be reachable
            without an account — the sign-in page is exactly where someone
            needs to read the terms they are about to accept. */}
        <Footer />
      </div>
    </>
  );
}

export default App;